import React, { useEffect, useRef, useState } from 'react';
import { Search, X, UserPlus } from 'lucide-react';
import { useListPatientsQuery } from '../api/patientsApi';
import './patientCombobox.css';

/**
 * Patient autocomplete combobox.
 * Searches by name / phone / patient code as the user types.
 *
 * Props:
 *   value:        the currently selected patient ({ id, fullName, ... }) or null
 *   onChange:     (patient | null) => void
 *   onCreateNew:  (initialQuery: string) => void  — called when the user clicks "+ Add new patient"
 *   placeholder:  string
 *   autoFocus:    bool
 */
export default function PatientCombobox({ value, onChange, onCreateNew, placeholder, autoFocus }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce the search query (250ms)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isFetching } = useListPatientsQuery(
    { page: 1, pageSize: 8, search: debounced },
    { skip: !!value || debounced.length < 2 }
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (autoFocus && !value && inputRef.current) inputRef.current.focus();
  }, [autoFocus, value]);

  const results = data?.items ?? [];
  const showCreateRow = debounced.length >= 2 && !isFetching && results.length === 0 && onCreateNew;

  // ── Selected state: show a card with a clear button ──
  if (value) {
    return (
      <div className="pcb-selected" ref={wrapRef}>
        <div className="pcb-selected-avatar">
          {(value.fullName ?? '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div className="pcb-selected-info">
          <div className="pcb-selected-name">
            {value.fullName}
            <span className="pcb-selected-code">{value.patientCode}</span>
          </div>
          <div className="pcb-selected-meta">
            {value.phoneNumber || '—'}{value.city ? ` · ${value.city}` : ''}
          </div>
        </div>
        <button
          type="button"
          className="pcb-clear"
          onClick={() => { onChange(null); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); }}
          title="Clear and search again"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // ── Search state: input + dropdown ──
  return (
    <div className="pcb-wrap" ref={wrapRef}>
      <div className="pcb-input-wrap">
        <Search size={14} className="pcb-input-icon" />
        <input
          ref={inputRef}
          type="text"
          className="form-control pcb-input"
          placeholder={placeholder ?? 'Search by name, phone, or patient ID…'}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open && (query || isFetching) && (
        <div className="pcb-dropdown">
          {isFetching && debounced.length >= 2 && (
            <div className="pcb-hint">Searching…</div>
          )}

          {!isFetching && debounced.length < 2 && (
            <div className="pcb-hint">Type at least 2 characters…</div>
          )}

          {!isFetching && results.map((p) => {
            const initials = (p.fullName ?? '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
            return (
              <button
                type="button"
                key={p.id}
                className="pcb-row"
                onClick={() => { onChange(p); setOpen(false); setQuery(''); }}
              >
                <div className="pcb-row-avatar">{initials}</div>
                <div className="pcb-row-main">
                  <div className="pcb-row-name">
                    {p.fullName}
                    <span className="pcb-row-code">{p.patientCode}</span>
                  </div>
                  <div className="pcb-row-meta">
                    {p.phoneNumber || '—'}{p.city ? ` · ${p.city}` : ''}
                  </div>
                </div>
              </button>
            );
          })}

          {showCreateRow && (
            <button
              type="button"
              className="pcb-create-row"
              onClick={() => { onCreateNew(query); setOpen(false); }}
            >
              <UserPlus size={14} /> Add new patient &ldquo;{query.trim()}&rdquo;
            </button>
          )}
        </div>
      )}
    </div>
  );
}
